import { SearchResultsRow } from "./SearchResultsRow";
import { useEffect, useMemo, useCallback, useState } from "react";
import { useAtlasSearch, useSearchEmbeddings } from "@labeling-tools/api/hooks";
import { JobTaskDetailSchema } from "@augmodo/lebron-browser-client";
import { useAppSelector } from "@/store/hooks";
import { Button, Center, Loader, Text } from "@mantine/core";
import { PRODUCT_ASSOCIATION_MODEL_ID } from "@/config/data";

const RESULTS_LIMIT = 50;
const RESULTS_PER_PAGE = 12;

export const ResultsGrid = ({
  task,
  embedding,
  embeddingLoading,
  mode,
}: {
  task: JobTaskDetailSchema;
  embedding: number[];
  embeddingLoading: boolean;
  mode: "admin" | "viewer" | "qa";
}) => {
  const searchTerms = useAppSelector(
    (state) => state.productAssociation.searchTerms,
  );
  const [visibleCount, setVisibleCount] = useState(RESULTS_PER_PAGE);

  // Memoize the current search term to prevent unnecessary re-renders
  const currentSearchTerm = useMemo(() => {
    return searchTerms.length > 0 ? searchTerms[searchTerms.length - 1] : null;
  }, [searchTerms]);

  // Reset visible count when search term changes
  useEffect(() => {
    setVisibleCount(RESULTS_PER_PAGE);
  }, [currentSearchTerm]);

  // const {
  //   mutate: searchCapturedImageEmbeddings,
  //   data: searchCapturedImageEmbeddingsData,
  //   isPending: searchCapturedImageEmbeddingsLoading,
  // } = useSearchEmbeddings();
  const {
    mutate: searchStockImageEmbeddings,
    data: searchStockImageEmbeddingsData,
    isPending: searchStockImageEmbeddingsLoading,
  } = useSearchEmbeddings();

  const { data: atlasSearchData, isLoading: atlasSearchLoading } =
    useAtlasSearch(currentSearchTerm);
  const searchProductIds = useMemo(
    () =>
      atlasSearchData?.products
        ?.map((product) => product.id)
        .filter((id): id is string => id != null) || null,
    [atlasSearchData?.products],
  );

  const fetchEmbeddings = useCallback(
    (entityIds: string[] | null = null) => {
      if (embedding?.length) {
        // the first row of images are captured images
        // these are real images from badges
        // TODO: we will support this soon after initial launch
        // searchCapturedImageEmbeddings({
        //   target: embedding,
        //   model_id: "019a0bce-6abd-059f-f439-86823fd36b59",
        //   types: ["PRODUCT_CAPTURED_IMAGE"],
        //   entity_ids: entityIds,
        //   limit: RESULTS_LIMIT,
        // });

        // the second row of images are stock images OR text about the product
        searchStockImageEmbeddings({
          target: embedding,
          model_id: PRODUCT_ASSOCIATION_MODEL_ID,
          types: ["PRODUCT_CATALOG_IMAGE", "PRODUCT_CATALOG_TEXT"],
          entity_ids: entityIds,
          limit: RESULTS_LIMIT,
        });
      }
    },
    [searchStockImageEmbeddings, embedding],
  );

  // Initial load - fetch embeddings without entity_ids filter
  useEffect(() => {
    if (task?.data?.image_id && searchTerms.length === 0) {
      fetchEmbeddings(null);
    }
  }, [task?.data?.image_id, searchTerms.length, fetchEmbeddings]);

  // Search-based load - fetch embeddings with entity_ids filter
  useEffect(() => {
    if (task?.data?.image_id && searchProductIds) {
      if (currentSearchTerm) {
        fetchEmbeddings(searchProductIds);
      } else {
        fetchEmbeddings(null);
      }
    }
  }, [
    task?.data?.image_id,
    searchProductIds,
    fetchEmbeddings,
    currentSearchTerm,
  ]);

  // Check if we're waiting for embeddings to be fetched after search completes
  const isWaitingForEmbeddings =
    searchProductIds &&
    !searchStockImageEmbeddingsLoading &&
    !searchStockImageEmbeddingsData;

  // All results from API
  const allResults = searchStockImageEmbeddingsData?.embeddings ?? [];
  // Only show up to visibleCount
  const visibleResults = allResults.slice(0, visibleCount);
  const hasMoreToShow = visibleCount < allResults.length;

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + RESULTS_PER_PAGE);
  };

  const isLoading =
    searchStockImageEmbeddingsLoading ||
    atlasSearchLoading ||
    embeddingLoading ||
    isWaitingForEmbeddings;

  if (isLoading) {
    return (
      <Center className="h-[300px] w-full">
        <Loader />
      </Center>
    );
  }

  return (
    <div className="pt-8 max-h-[calc(100vh-200px)] overflow-y-auto pb-16">
      <div>
        {allResults.length === 0 ? (
          <Text>No results found</Text>
        ) : (
          <Text c="gray.7">
            Select a product that matches the image to the left
          </Text>
        )}
      </div>
      <div className="space-y-8 pt-4">
        <SearchResultsRow results={visibleResults} mode={mode} task={task} />
        {hasMoreToShow &&
          !atlasSearchLoading &&
          !searchStockImageEmbeddingsLoading && (
            <div className="flex justify-center pb-4">
              <Button variant="outline" onClick={handleLoadMore}>
                Load More
              </Button>
            </div>
          )}
      </div>
    </div>
  );
};
